/* src/components/UI/NetworkStatus.jsx */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

const NetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showStatus, setShowStatus] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setShowStatus(true);
        setTimeout(() => setShowStatus(false), 3000);
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowStatus(true);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const statusVariants = {
    hidden: {
      opacity: 0,
      y: 100,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      y: 100,
      scale: 0.8,
      transition: {
        duration: 0.3
      }
    }
  };

  const iconVariants = {
    online: {
      scale: [1, 1.2, 1],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    },
    offline: {
      x: [-2, 2, -2, 2, 0],
      transition: {
        duration: 0.5,
        ease: "easeInOut"
      }
    }
  };

  return (
    <AnimatePresence>
      {showStatus && (
        <motion.div
          className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-full shadow-lg backdrop-blur-sm flex items-center space-x-3"
          style={{
            backgroundColor: isOnline 
              ? `${theme.colors.success}15` 
              : `${theme.colors.error}15`,
            border: `1px solid ${isOnline ? theme.colors.success : theme.colors.error}`,
            color: isOnline ? theme.colors.success : theme.colors.error
          }}
          variants={statusVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="text-lg"
            variants={iconVariants}
            animate={isOnline ? "online" : "offline"}
          >
            {isOnline ? '🟢' : '🔴'}
          </motion.div>

          <span className="font-medium text-sm">
            {isOnline ? 'Back online' : 'Connection lost'}
          </span>

          <motion.button
            className="ml-2 text-lg opacity-70 hover:opacity-100 transition-opacity"
            onClick={() => setShowStatus(false)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
export default NetworkStatus;